import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  ReceiptText,
  Languages,
  KeySquare,
  Split,
  Layers
} from 'lucide-react';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2.tsx';
import ButtonGroup, {
  type ButtonGroupItem
} from '@components/atoms/ui-kit/button-group.tsx';
import { capitalizeFirstLetter } from '@utils/capitalize.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useOrgCurrency } from '@utils/hooks/use-org-currency';
import { formatCurrency } from '@utils/helpers/currency';

import {
  type UsageResult,
  useGetCustomerUsage
} from '@utils/api/resellers/use-get-usages.tsx';

type ViewType = 'invoice' | 'day' | 'week' | 'month' | 'year';
type SplitType = '' | 'period' | 'language' | 'token' | 'subscription';

type UsageRow = {
  usage_period_start: string;
  usage_period_end: string;
  units_used: number;
  token_name: string;
  language: string;
  cost: number | string;
};

type UsageDetailsProps = { resellerId?: string };

export const UsageDetails = ({ resellerId }: UsageDetailsProps) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const currency = useOrgCurrency();

  const [viewType, setViewType] = useState<ViewType>('invoice');
  const [splitType, setSplitType] = useState<SplitType>('');

  const { data, isLoading } = useGetCustomerUsage(resellerId || '', {
    view: viewType,
    split_by: splitType
  });

  const viewItems: ButtonGroupItem[] = useMemo(
    () => [
      {
        icon: <ReceiptText />,
        label: t('invoice'),
        onClick: () => setViewType('invoice'),
        variant: viewType === 'invoice' ? 'primary' : 'outline'
      },
      {
        icon: <CalendarDays />,
        label: t('day'),
        onClick: () => setViewType('day'),
        variant: viewType === 'day' ? 'primary' : 'outline'
      },
      {
        icon: <CalendarRange />,
        label: t('week'),
        onClick: () => setViewType('week'),
        variant: viewType === 'week' ? 'primary' : 'outline'
      },
      {
        icon: <Calendar />,
        label: t('month'),
        onClick: () => setViewType('month'),
        variant: viewType === 'month' ? 'primary' : 'outline'
      },
      {
        icon: <CalendarClock />,
        label: t('year'),
        onClick: () => setViewType('year'),
        variant: viewType === 'year' ? 'primary' : 'outline'
      }
    ],
    [t, viewType]
  );

  const splitItems: ButtonGroupItem[] = useMemo(
    () => [
      {
        icon: <Split />,
        label: t('none'),
        onClick: () => setSplitType(''),
        variant: splitType === '' ? 'primary' : 'outline'
      },
      {
        icon: <CalendarRange />,
        label: t('period'),
        onClick: () => setSplitType('period'),
        variant: splitType === 'period' ? 'primary' : 'outline'
      },
      {
        icon: <Languages />,
        label: t('language'),
        onClick: () => setSplitType('language'),
        variant: splitType === 'language' ? 'primary' : 'outline'
      },
      {
        icon: <KeySquare />,
        label: t('token'),
        onClick: () => setSplitType('token'),
        variant: splitType === 'token' ? 'primary' : 'outline'
      },
      {
        icon: <Layers />,
        label: t('subscription'),
        onClick: () => setSplitType('subscription'),
        variant: splitType === 'subscription' ? 'primary' : 'outline'
      }
    ],
    [t, splitType]
  );

  const columns: DataGridColumn<UsageRow>[] = [
    {
      id: 'usage_period_start',
      header: t('period-start'),
      accessorKey: 'usage_period_start',
      minWidth: 140
    },
    {
      id: 'usage_period_end',
      header: t('period-end'),
      accessorKey: 'usage_period_end',
      minWidth: 140
    },
    {
      id: 'units_used',
      header: t('units-used'),
      accessorKey: 'units_used',
      minWidth: 120,
      cell: ({ getValue }) => String(getValue() ?? 0)
    },
    {
      id: 'token_name',
      header: t('token'),
      accessorKey: 'token_name',
      minWidth: 140,
      cell: ({ getValue }) => String(getValue() ?? '-')
    },
    {
      id: 'language',
      header: t('language'),
      accessorKey: 'language',
      minWidth: 160,
      cell: ({ getValue }) => String(getValue() ?? '-')
    },
    {
      id: 'cost',
      header: `${t('cost')} (${currency})`,
      accessorKey: 'cost',
      minWidth: 120,
      cell: ({ getValue }) => {
        const v = getValue();
        return typeof v === 'number'
          ? formatCurrency(v, currency)
          : String(v ?? '-');
      }
    }
  ];

  const rows: UsageRow[] = useMemo(() => {
    const list = data?.result ?? [];
    return list.map((u: UsageResult) => ({
      language: capitalizeFirstLetter(u.language),
      units_used: u.units_used,
      usage_period_start: u.usage_period_start,
      usage_period_end: u.usage_period_end,
      token_name: u.token_name,
      cost: u.cost
    }));
  }, [data]);

  return (
    <div className="p-4" style={{ color: getColour('normalText') }}>
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2">
          <span
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('view')}
          </span>
          <ButtonGroup items={viewItems} size="medium" variant="outline" />
        </div>

        <div className="flex flex-col gap-2">
          <span
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('split')}
          </span>
          <ButtonGroup items={splitItems} size="medium" variant="outline" />
        </div>
      </div>

      <div
        className="w-full h-px my-4"
        style={{ backgroundColor: getColour('border') }}
      />

      <DataGridV2<UsageRow>
        data={rows}
        columns={columns}
        enableRowSelection={false}
        enablePagination
        manualPagination
        enableSorting={false}
        manualSorting={false}
        enableFiltering={false}
        manualFiltering={false}
        totalItems={data?.total_results ?? 0}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        showColumnFilters={false}
        filterMode="popover"
        height="55vh"
        dense
        grid={false}
        striped
        loading={isLoading || !data}
        getRowId={(row, idx) =>
          `${row.usage_period_start}-${row.usage_period_end}-${row.token_name}-${idx}`
        }
      />
    </div>
  );
};
